// Icons
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";
import { CompoundPermission } from "context/Ability";
import { urlPaths } from "enums/urlPaths";
import { ChartRouteNames } from "enums/chartEnums";
import {
    updateLDDashboardItem,
    updateLDOrgDashboardItem,
    updateLDBriefItem,
    updateLDSearchAiItem,
    updatePaceItems,
} from "./mainMenu.functions";

export type MenuItem = {
    id: string;
    label: string;
    startIcon: React.ReactNode | null;
    endIcon: React.ReactNode | null;
    disabled: boolean;
    isSelected: boolean;
    permissions: CompoundPermission[];
    verticalSpace?: number;
    parentId?: string;
    slide?: number;
    goToSlide?: number;
    indent?: number;
    shouldExpand?: boolean;
    isExpanded?: boolean;
    base?: string;
    path?: string;
    url?: string;
    topSeparator?: boolean;
    bottomSeparator?: boolean;
    extraPaths?: string[];
    updatePathAndItems?: any;
    children?: MenuItem[][];
};

export const menuConfig: MenuItem[] = [
    {
        id: "hub-management",
        slide: 0,
        label: "Hub Management",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        url: urlPaths.HubManagement,
        base: urlPaths.HubManagement,
        path: "",
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
    },
    {
        id: "user-management",
        slide: 0,
        label: "User Management",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        url: urlPaths.UserManagement,
        base: urlPaths.UserManagement,
        path: "",
        permissions: [{ list: ["SYS_ADMIN_VIEW_USER_MANAGEMENT"], operator: "or" }],
    },
    {
        id: "my-dashboard",
        slide: 0,
        label: "My Dashboard",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/my-dashboard",
        url: urlPaths.PaceLabsDashboard,
        extraPaths: ["/pace/assessment", "pace/my-pace", "/pace/report", "/pace/my-experience", "/pace/my-insights"],
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_DASHBOARD"], operator: "and" },
        ],
    },
    {
        id: "pace-assessment",
        slide: 0,
        label: "PACE Assessment",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.paceIconColouredDark} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: 3,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/report",
        url: "/pace/report/user",
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN", "PACE_REPORT_RUN_GRP"], operator: "or" },
            { list: ["SAAS_PACE", "SAAS_PACE_ASSESSMENT"], operator: "and" },
        ],
    },
    {
        id: "my-pace",
        slide: 0,
        label: "My PACE",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: 3,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/pace",
        path: "/my-pace",
        url: urlPaths.MyPace,
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PACE_REPORT"], operator: "and" },
        ],
    },
    {
        id: "my-experience",
        slide: 0,
        label: "My Experience",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.sparkles} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: 3,
        isSelected: false,
        disabled: false,
        verticalSpace: 2,
        base: "/pace",
        path: "/my-experience",
        url: urlPaths.MyExperience,
        extraPaths: ["/pace/my-insights"],
        updatePathAndItems: updatePaceItems,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_INSIGHTS"], operator: "and" },
        ],
    },
    {
        id: "pace-changer-0",
        slide: 0,
        goToSlide: 2,
        label: "PACE",
        parentId: undefined,
        startIcon: undefined,
        endIcon: <CustomIcon icon={icons.arrowRight} sx={{ fontSize: 10 }} />,
        disabled: false,
        isSelected: false,
        verticalSpace: 2,
        permissions: [
            { list: ["SYS_ADMIN_VIEW_PACE_PORTAL", "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" },
        ],
    },
    {
        id: "ld-changer-0",
        slide: 0,
        goToSlide: 1,
        label: "LEADERSHIP DYNAMICS",
        parentId: undefined,
        startIcon: undefined,
        endIcon: <CustomIcon icon={icons.arrowRight} sx={{ fontSize: 10 }} />,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_COMPANY"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
    },
    // Leadership Dynamics
    {
        id: "ld-changer-1",
        slide: 1,
        goToSlide: 0,
        label: "LEADERSHIP DYNAMICS",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.arrowLeft} sx={{ fontSize: 10 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_COMPANY"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
    },

    {
        id: "select-org",
        slide: 1,
        label: "Select Org",
        indent: 3,
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.circledPlus} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        bottomSeparator: true,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/leadership-dynamics",
        path: "/my-searches",
        url: urlPaths.MySearches,
        permissions: [{ list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "SAAS_LD_PROJECT"], operator: "or" }],
    },
    {
        id: "org-dashboard",
        slide: 1,
        label: "Org Dashboard",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuLD} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: true,
        isSelected: false,
        verticalSpace: 1,
        base: "/leadership-dynamics",
        path: "/dashboard",
        url: urlPaths.ProjectDashboard,
        updatePathAndItems: updateLDOrgDashboardItem,
        permissions: [
            { list: ["LD_SEARCH_COMPANY", "LD_PROJECT_VIEW"], operator: "and" },
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
    },
    {
        id: "ld-dashboard",
        slide: 1,
        label: "Leadership Dashboard",
        indent: 1,
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16, color: "common.white" }} />,
        endIcon: undefined,
        disabled: true,
        isSelected: false,
        shouldExpand: true,
        isExpanded: true,
        verticalSpace: 1,
        extraPaths: [
            "/leadership-dashboard/overview",
            "/leadership-dashboard/management",
            "/leadership-dashboard/path-to-improvement",
            "/leadership-dynamics/insights/",
            urlPaths.Domain,
            urlPaths.Team,
            urlPaths.Vcp,
        ],
        updatePathAndItems: updateLDDashboardItem,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            { list: ["SAAS_LD_PROJECT"], operator: "or" },
        ],
        children: [
            [
                {
                    id: "ld-overview",
                    label: "Overview",
                    indent: 3.5,
                    parentId: "ld-dashboard",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dashboard",
                    path: "/overview",
                    url: "/leadership-dashboard/overview",
                    permissions: [
                        {
                            list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: "ld-management",
                    label: "Management & Board Assessment",
                    indent: 3.5,
                    parentId: "ld-dashboard",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dashboard",
                    path: "/management",
                    url: "/leadership-dashboard/management",
                    permissions: [
                        {
                            list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                            operator: "or",
                        },
                    ],
                },
                {
                    id: "ld-path-to-improvement",
                    label: "Path to Improvement",
                    indent: 3.5,
                    parentId: "ld-dashboard",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    base: "/leadership-dashboard",
                    path: "/path-to-improvement",
                    url: "/leadership-dashboard/path-to-improvement",
                    permissions: [
                        {
                            list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                            operator: "or",
                        },
                    ],
                },
            ],
            [
                {
                    id: "ld-insights",
                    slide: 1,
                    label: "Insights",
                    indent: 4,
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    shouldExpand: true,
                    isExpanded: false,
                    verticalSpace: 1,
                    topSeparator: true,
                    extraPaths: [
                        "/leadership-dashboard/overview",
                        "/leadership-dashboard/management",
                        "/leadership-dashboard/path-to-improvement",
                        "/leadership-dynamics/insights/",
                        urlPaths.Domain,
                        urlPaths.Team,
                        urlPaths.Vcp,
                        "/leadership-dynamics/insights/{companyId}/{childId}",
                    ],
                    permissions: [
                        {
                            list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT"],
                            operator: "or",
                        },
                        { list: ["SAAS_LD_PROJECT"], operator: "or" },
                    ],
                    children: [
                        [
                            {
                                id: ChartRouteNames.Balance,
                                indent: 4,
                                label: "Balance",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Positioning,
                                indent: 4,
                                label: "Individual Positioning",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Behavioural,
                                indent: 4,
                                label: "Behaviours",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.BehaviouralBreakdown,
                                indent: 4,
                                label: "Behavioural Breakdown",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Tenure,
                                indent: 4,
                                label: "Tenure",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    { list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT"], operator: "or" },
                                ],
                            },
                            {
                                id: ChartRouteNames.Potential,
                                indent: 4,
                                label: "Potential",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Comparison,
                                indent: 4,
                                label: "Comparison",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Situational,
                                indent: 4,
                                label: "Situational",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Domain,
                                indent: 4,
                                label: "Domain",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Functional,
                                indent: 4,
                                label: "Functional",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                            {
                                id: ChartRouteNames.Overall,
                                indent: 4,
                                label: "Overall LSPM",
                                parentId: "ld-insights",
                                startIcon: undefined,
                                endIcon: undefined,
                                disabled: true,
                                isSelected: false,
                                base: "/leadership-dynamics",
                                path: "/insights",
                                url: "/leadership-dynamics/insights",
                                permissions: [
                                    {
                                        list: [
                                            "LD_FREE",
                                            "LD_LIMITED",
                                            "LD_BASIC",
                                            "LD_PROJECT_EDIT",
                                            "LD_TALENT",
                                            "SAAS_LD_PROJECT",
                                        ],
                                        operator: "or",
                                    },
                                ],
                            },
                        ],
                    ],
                },
            ],
        ],
    },
    {
        id: "leadership-brief",
        slide: 1,
        label: "Leadership Brief",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.documentLightBulb} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: 1,
        disabled: true,
        isSelected: false,
        base: "/leadership-dynamics",
        path: "/brief",
        url: urlPaths.Brief,
        verticalSpace: 1,
        updatePathAndItems: updateLDBriefItem,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
    },
    {
        id: "search-ai",
        slide: 1,
        label: "Search AI",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.flashLight} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        indent: 1,
        isSelected: false,
        shouldExpand: true,
        isExpanded: false,
        disabled: true,
        verticalSpace: 1,
        extraPaths: ["/talent/companies", "/talent/people", "/talent/refinement"],
        updatePathAndItems: updateLDSearchAiItem,
        permissions: [
            { list: ["LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT_SEARCH_AI_CREATE"], operator: "and" },
        ],
        children: [
            [
                {
                    id: "company-search",
                    label: "Company Search",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    indent: 4,
                    path: "/companies",
                    url: "/talent/companies",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_COMPANY"], operator: "or" },
                    ],
                },
                {
                    id: "people-search",
                    label: "People Search",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    indent: 4,
                    path: "/people",
                    url: "/talent/people",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_PEOPLE"], operator: "or" },
                    ],
                },
                {
                    id: "refinement",
                    label: "Refinement",
                    parentId: "search-ai",
                    startIcon: undefined,
                    endIcon: undefined,
                    disabled: true,
                    isSelected: false,
                    verticalSpace: 1,
                    base: "/talent",
                    indent: 4,
                    path: "/refinement",
                    url: "/talent/refinement",
                    permissions: [
                        { list: ["LD_TALENT"], operator: "or" },
                        { list: ["SAAS_LD_PROJECT_SEARCH_AI_REFINE"], operator: "or" },
                    ],
                },
            ],
        ],
    },
    // PACE
    {
        id: "pace-changer-1",
        slide: 2,
        goToSlide: 0,
        label: "PACE",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.arrowLeft} sx={{ fontSize: 10 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        permissions: [
            { list: ["PACE"], operator: "or" },
            { list: ["SAAS_PACE"], operator: "or" },
            { list: ["SYS_ADMIN_VIEW_PACE_PORTAL", "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" },
        ],
    },
    // {
    //     id: "my-dashboard-1",
    //     slide: 2,
    //     label: "My Dashboard",
    //     parentId: undefined,
    //     startIcon: <CustomIcon icon={icons.mainMenuDashboard} sx={{ fontSize: 16 }} />,
    //     endIcon: undefined,
    //     disabled: false,
    //     isSelected: false,
    //     verticalSpace: 1,
    //     base: "/pace",
    //     path: "/my-dashboard",
    //     url: urlPaths.PaceLabsDashboard,
    //     extraPaths: ["/pace/behavioural-comparison"],
    //     updatePathAndItems: updatePaceItems,
    //     permissions: [{ list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" }],
    // },
    {
        id: "behavioural-comparison",
        slide: 2,
        label: "Behavioural Comparison",
        parentId: undefined,
        startIcon: undefined,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        indent: 3,
        base: "/behavioural-comparison",
        path: "/pace",
        url: "/pace/behavioural-comparison",
        updatePathAndItems: updatePaceItems,
        permissions: [{ list: ["SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" }],
    },
    {
        id: "admin",
        slide: 2,
        label: "PACE Portal",
        parentId: undefined,
        startIcon: <CustomIcon icon={icons.equalizer} sx={{ fontSize: 16 }} />,
        endIcon: undefined,
        disabled: false,
        isSelected: false,
        verticalSpace: 1,
        base: "/admin",
        path: "/pace",
        url: "/pace/admin",
        updatePathAndItems: updatePaceItems,
        permissions: [{ list: ["SYS_ADMIN_VIEW_PACE_PORTAL"], operator: "or" }],
    },
];
